import { api } from './api';

const basePath = ({ organizationId, projectId, resourceId }) => (
  `/v1/organizations/${encodeURIComponent(organizationId)}`
  + `/projects/${encodeURIComponent(projectId)}`
  + `/resources/${encodeURIComponent(resourceId)}/inventory-metadata`
);

export const inventoryMetadataPath = (scope) => basePath(scope);
export const inventoryMetadataHistoryPath = (scope) => `${basePath(scope)}/history`;

export const inventoryMetadataApi = {
  get: (scope) => api.get(inventoryMetadataPath(scope)),
  update: (scope, expectedRevision, metadata) => api.patch(inventoryMetadataPath(scope), { expected_revision: expectedRevision, metadata }),
  history: (scope, limit = 100) => api.get(inventoryMetadataHistoryPath(scope), { params: { limit } }),
};

export const ENVIRONMENTS = ['unclassified', 'development', 'test', 'staging', 'production', 'sandbox', 'other'];
export const CRITICALITIES = ['unclassified', 'low', 'medium', 'high', 'critical'];
export const LIFECYCLE_STATES = ['unclassified', 'active', 'temporary', 'planned_retirement', 'retired'];
export const DATA_CLASSIFICATIONS = ['unclassified', 'public', 'internal', 'confidential', 'restricted'];
export const OWNER_KINDS = ['user', 'team', 'service'];
export const LINK_KINDS = ['documentation', 'runbook', 'dashboard', 'repository', 'ticket', 'other'];
export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const emptyInventoryMetadata = (resourceId = '') => ({
  resource_id: resourceId, purpose: '', description: '', environment: 'unclassified',
  criticality: 'unclassified', service_owner: null, technical_owners: [], cost_center: '',
  lifecycle_state: 'unclassified', expires_at: null, maintenance_window: null,
  data_classification: 'unclassified', links: [], tags: [], revision: 0,
  source: 'implicit_default', evidence_status: 'recorded',
});

export const normalizeInventoryMetadata = (payload = {}, resourceId = '') => ({
  ...emptyInventoryMetadata(resourceId),
  ...(payload.metadata || payload),
  technical_owners: (payload.metadata || payload).technical_owners || [],
  links: (payload.metadata || payload).links || [],
  tags: (payload.metadata || payload).tags || [],
});

const localDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export const metadataToForm = (metadata) => ({
  purpose: metadata.purpose || '', description: metadata.description || '',
  environment: metadata.environment || 'unclassified', criticality: metadata.criticality || 'unclassified',
  service_owner: metadata.service_owner ? { ...metadata.service_owner } : null,
  technical_owners: (metadata.technical_owners || []).map((owner) => ({ ...owner })),
  cost_center: metadata.cost_center || '', lifecycle_state: metadata.lifecycle_state || 'unclassified',
  expires_at: localDateTime(metadata.expires_at),
  maintenance_window: metadata.maintenance_window ? { ...metadata.maintenance_window, days: [...metadata.maintenance_window.days] } : null,
  data_classification: metadata.data_classification || 'unclassified',
  links: (metadata.links || []).map((link) => ({ ...link })), tags: (metadata.tags || []).join(', '),
});

const cleanOwner = (owner) => owner?.id?.trim() ? {
  kind: owner.kind, id: owner.id.trim(), ...(owner.display_name?.trim() ? { display_name: owner.display_name.trim() } : {}),
} : null;

export const formToMetadata = (form) => ({
  purpose: form.purpose.trim(), description: form.description.trim(), environment: form.environment,
  criticality: form.criticality, service_owner: cleanOwner(form.service_owner),
  technical_owners: form.technical_owners.map(cleanOwner).filter(Boolean), cost_center: form.cost_center.trim(),
  lifecycle_state: form.lifecycle_state,
  expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
  maintenance_window: form.maintenance_window ? {
    timezone: form.maintenance_window.timezone.trim(), days: form.maintenance_window.days,
    start: form.maintenance_window.start, duration_minutes: Number(form.maintenance_window.duration_minutes),
  } : null,
  data_classification: form.data_classification,
  links: form.links.map((link) => ({ kind: link.kind, label: link.label.trim(), url: link.url.trim() })).filter((link) => link.label || link.url),
  tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
});

export const changedInventoryMetadata = (current, next) => Object.fromEntries(
  Object.entries(next).filter(([field, value]) => JSON.stringify(current?.[field] ?? null) !== JSON.stringify(value)),
);

const ownerValid = (owner) => !owner || (OWNER_KINDS.includes(owner.kind) && /^[A-Za-z0-9._:@/-]{1,160}$/.test(owner.id || '') && (owner.display_name || '').trim().length <= 160);
const safeLink = (link) => {
  try {
    const url = new URL(link.url);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password && !url.search && !url.hash;
  } catch (_) { return false; }
};

export const validateInventoryMetadataForm = (form) => {
  const errors = {};
  if (form.purpose.trim().length > 240) errors.purpose = 'Use 240 characters or fewer.';
  if (form.description.trim().length > 4000) errors.description = 'Use 4,000 characters or fewer.';
  if (!ENVIRONMENTS.includes(form.environment)) errors.environment = 'Choose a valid environment.';
  if (!CRITICALITIES.includes(form.criticality)) errors.criticality = 'Choose a valid criticality.';
  if (!ownerValid(form.service_owner)) errors.service_owner = 'Choose an owner type and enter a valid owner ID.';
  if (form.technical_owners.length > 20 || form.technical_owners.some((owner) => !ownerValid(owner))) errors.technical_owners = 'Enter at most 20 valid technical owners.';
  if (form.cost_center.trim().length > 120) errors.cost_center = 'Use 120 characters or fewer.';
  if (!LIFECYCLE_STATES.includes(form.lifecycle_state)) errors.lifecycle_state = 'Choose a valid lifecycle state.';
  if (form.lifecycle_state === 'temporary' && !form.expires_at) errors.expires_at = 'Temporary resources require an expiry date and time.';
  if (form.expires_at && Number.isNaN(new Date(form.expires_at).getTime())) errors.expires_at = 'Enter a valid expiry date and time.';
  if (!DATA_CLASSIFICATIONS.includes(form.data_classification)) errors.data_classification = 'Choose a valid data classification.';
  if (form.maintenance_window) {
    try { new Intl.DateTimeFormat('en', { timeZone: form.maintenance_window.timezone }).format(); }
    catch (_) { errors.maintenance_window = 'Enter a valid IANA timezone such as Asia/Kolkata.'; }
    if (!form.maintenance_window.days.length || form.maintenance_window.days.some((day) => !DAYS.includes(day))) errors.maintenance_window = 'Choose at least one valid maintenance day.';
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(form.maintenance_window.start)) errors.maintenance_window = 'Enter a valid maintenance start time.';
    const duration = Number(form.maintenance_window.duration_minutes);
    if (!Number.isInteger(duration) || duration < 15 || duration > 1440) errors.maintenance_window = 'Maintenance duration must be 15–1,440 minutes.';
  }
  if (form.links.length > 30 || form.links.some((link) => !LINK_KINDS.includes(link.kind) || !link.label.trim() || link.label.trim().length > 120 || !safeLink(link))) errors.links = 'Use at most 30 safe HTTP(S) links without credentials, query strings, or fragments.';
  const tags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
  if (tags.length > 50 || tags.some((tag) => !/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,79}$/.test(tag))) errors.tags = 'Use at most 50 tags containing letters, numbers, dots, underscores, colons, slashes, or hyphens.';
  return errors;
};

export const metadataImpactSummary = (metadata) => {
  if (!metadata) return [];
  const owner = metadata.service_owner?.display_name || metadata.service_owner?.id;
  const items = [];
  if (metadata.purpose) items.push(`Purpose: ${metadata.purpose}`);
  if (owner) items.push(`Service owner: ${owner}`);
  if (metadata.technical_owners?.length) items.push(`Technical owners: ${metadata.technical_owners.map((item) => item.display_name || item.id).join(', ')}`);
  if (metadata.environment && metadata.environment !== 'unclassified') items.push(`Environment: ${metadata.environment}`);
  if (metadata.criticality && metadata.criticality !== 'unclassified') items.push(`Criticality: ${metadata.criticality}`);
  if (metadata.lifecycle_state && metadata.lifecycle_state !== 'unclassified') items.push(`Lifecycle: ${metadata.lifecycle_state}`);
  if (metadata.expires_at) items.push(`Expiry: ${new Date(metadata.expires_at).toLocaleString()}`);
  return items;
};
